/**
 * Feeds Client
 *
 * Provides feed-related API methods using the SteemWorld SDK.
 * Also includes Steemit API methods for single post fetching.
 */

import type { FeedsAPIResponse, FeedsQueryParams } from '@/types/feed'
import {
  getAccountFriendsFeed as sdkGetAccountFriendsFeed,
  getAccountPosts as sdkGetAccountPosts,
  getAccountBlog as sdkGetAccountBlog,
  getPostsByAuthor as sdkGetPostsByAuthor,
  type FeedsApiResponse,
} from '@/services/steemworld'

// Re-export SDK functions with type compatibility
export async function getAccountFriendsFeed(
  account: string,
  params: FeedsQueryParams = {}
): Promise<FeedsAPIResponse> {
  const response = await sdkGetAccountFriendsFeed(account, params)
  return response as FeedsAPIResponse
}

export async function getAccountPosts(
  account: string,
  params: FeedsQueryParams = {}
): Promise<FeedsAPIResponse> {
  const response = await sdkGetAccountPosts(account, params)
  return response as FeedsAPIResponse
}

export async function getAccountBlog(
  account: string,
  params: FeedsQueryParams = {}
): Promise<FeedsAPIResponse> {
  const response = await sdkGetAccountBlog(account, params)
  return response as FeedsAPIResponse
}

export async function getPostsByAuthor(
  author: string,
  params: FeedsQueryParams = {}
): Promise<FeedsAPIResponse> {
  const response = await sdkGetPostsByAuthor(author, params)
  return response as FeedsAPIResponse
}

// Steemit API types (not part of SteemWorld SDK)
export interface SteemitPostResponse {
  jsonrpc: string
  result: SteemitPost
  id: number
}

export interface SteemitPost {
  post_id: number
  author: string
  permlink: string
  category: string
  title: string
  body: string
  json_metadata: string
  created: string
  last_update: string
  depth: number
  children: number
  net_rshares: number
  last_payout: string
  cashout_time: string
  total_payout_value: string
  curator_payout_value: string
  pending_payout_value: string
  total_vote_weight: number
  author_reputation: number
  promoted: string
  root_author: string
  root_permlink: string
  max_accepted_payout: string
  percent_steem_dollars: number
  allow_replies: boolean
  allow_votes: boolean
  allow_curation_rewards: boolean
  beneficiaries: Array<{ account: string; weight: number }>
  url: string
  root_title: string
  active_votes: Array<{
    voter: string
    weight: number
    rshares: number
    percent: number
    reputation: number
    time: string
  }>
}

/**
 * Fetches a single post by author and permlink using Steemit API
 *
 * Note: This uses the Steemit JSON-RPC API, not SteemWorld
 */
export async function getPost(author: string, permlink: string): Promise<SteemitPostResponse> {
  const response = await fetch('https://api.steemit.com', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      jsonrpc: '2.0',
      method: 'condenser_api.get_content',
      params: [author, permlink],
      id: 1,
    }),
  })

  if (!response.ok) {
    throw new Error(`Post API error: ${response.statusText}`)
  }

  return response.json()
}

// Re-export SDK types for convenience
export type { FeedsApiResponse }
